import { useSyncExternalStore } from 'react';
import { Flag, Calendar, ChevronRight, Trash2 } from '../../icons';
import type { MyTaskDto } from '../../api/tasks';
import { dueDayDiff, dueState } from './my-task-due';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './MyTasksRowExtras.css';

/**
 * ⚠ 정본(`feature/dev-syk`)에서 옮겨 왔다 (2026-10-01). 값·주석은 정본 그대로이고,
 *   더한 것은 이 머리말뿐이다.
 *   ⇒ 이 부품은 `./my-task-due` 에 기대는데 **그 파일이 되돌려져 있었다**. 그 사정과
 *     되돌림을 푼 까닭은 `my-task-due.ts` 머리말에 한 벌로 적었다 — 여기서 되풀이하지 않는다.
 *   ⚠ 지금 이 부품을 «쓰는 화면은 0곳»이다(2026-10-01 · origin/main · git grep).
 *     붙이는 일은 「내 할일 표」를 갈아끼우는 판이고, 그때 22건이 그대로 지킨다.
 *
 * MyTasksRowExtras (2026-08-27) — 내 업무 목록의 **남는 폭에 들어가는 것들**. 신규 파일.
 *
 * 표 폭을 1440px 로 묶고 열을 다섯으로 늘린 뒤에도 여백이 세 군데 남았다.
 * 그 자리를 지우는 대신 **쓸모 있는 것으로 채운다** — 여기 있는 넷이 각각 한 자리를 맡는다.
 *
 *   ① GroupSummary  — 그룹 헤더 줄. colSpan 으로 전 폭을 잡고 이름만 쓰던 줄이라 가장 크게 비어 있었다.
 *   (② 남은 기간 점 표시는 2026-08-27 사용자 지시로 제거했다 — 기한 열의 글자만으로 충분하다는 판단.)
 *   ③ RowActions    — 제목 셀 오른쪽. 평소엔 비어 있고 행에 손이 닿을 때만 나타난다.
 *                     (2026-09-01 — 넷째로 [🗑] 삭제가 붙었다. 옵셔널이라 콜백을 준 화면에만 뜬다.)
 *                     (2026-09-04 — 완료된 업무에는 계획 액션 둘([🚩]·[📅])을 그리지 않는다.)
 *   ④ TagCell       — 태그 열. **데이터가 있을 때만** 열이 선다(shouldShowTagColumn).
 *
 * 목록 로직(MyTasksBoard)은 건드리지 않는다 — 전부 표시 전용이고, 값을 바꾸는 것은
 * ③ 뿐인데 그것도 부모가 준 콜백을 부를 뿐이다.
 */

/* ============================================================
 * ① 그룹 요약 — 그룹 헤더 줄의 남는 폭
 * ============================================================ */

/** 그룹 한 덩어리를 한 줄로 요약한 값. 전부 이미 받아 둔 목록에서 센다(추가 조회 없음). */
export function summarizeGroup(rows: MyTaskDto[], now: number = Date.now()): {
  avgProgress: number;
  overdue: number;
  soonestDiff: number | null;
} {
  let sum = 0;
  let overdue = 0;
  let soonest: number | null = null;
  for (const t of rows) {
    /*
      ⚠ 이 «0~100 가두기»는 **정본 시험 22건 중 어느 것도 안 잠근다** (2026-10-01 사보타주로 확인:
        `Math.min(Math.max(…,0),100)` 을 `(t.progress ?? 0)` 으로 바꿔도 28건이 «전부 초록»).
        정본 시험의 입력이 100·50·없음뿐이라 가두기가 «일할 자리»가 없기 때문이다.
      ⇒ 가져온 시험을 손대지 않으려고 «그대로 두고 여기 적는다». 지금은 해가 없다 —
        서버가 이 칸을 아예 안 보내기 때문이다(api/tasks.ts 의 progress 주석 참조).

      만료 조건   **서버가 `progress` 를 보내기 시작하면**
                 (재는 법: `prisma/schema.prisma` 의 `model Task` 블록 안에 `progress` 가 생기면.
                  ⚠ 파일 전체를 세면 안 된다 — `in_progress`·`BroadcastProgress` 가 섞여 «5건»이 나온다.
                  블록만 잘라 세야 «0 / 1(priority 대조군)» 이 보인다. 2026-10-01 에 실제로 겪은 함정)
      그때 할 일   «범위 밖 값»(-5 · 120)을 주는 갈래를 시험에 하나 더한다.
                 그때는 이 한 줄이 화면의 막대 길이를 지키는 «유일한» 것이 된다.
    */
    sum += Math.min(Math.max(t.progress ?? 0, 0), 100);
    const ds = dueState(t.dueDate, t.status, now);
    if (ds === 'overdue') overdue += 1;
    if (t.dueDate) {
      const d = dueDayDiff(t.dueDate, now);
      // 지연(음수)까지 포함해 가장 급한 것을 고른다 — '가장 급하다' 는 이미 지난 것도 센다.
      if (!Number.isNaN(d) && (soonest === null || d < soonest)) soonest = d;
    }
  }
  return {
    avgProgress: rows.length ? Math.round(sum / rows.length) : 0,
    overdue,
    soonestDiff: soonest,
  };
}

/**
 * 그룹 헤더 줄 오른쪽에 붙는 요약.
 *
 * 왜 여기인가: 이 줄은 colSpan 으로 표 전 폭을 잡아 놓고 왼쪽 이름 한 조각만 쓰고 있었다.
 * 행마다 흩어진 여백과 달리 **한 줄 통째로** 비어서, 채워도 다른 곳을 밀어내지 않는다.
 */
export function GroupSummary({ rows }: { rows: MyTaskDto[] }) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  if (!rows.length) return null;
  const { avgProgress, overdue, soonestDiff } = summarizeGroup(rows);

  /*
   * 세 조각을 **각자 요약하는 열 아래**에 세운다(부모 .mtb-grow 가 표와 같은 grid).
   * 종전에는 float:right 로 셋을 오른쪽 끝에 몰아 놓았는데, 평균 진척률 막대가 진척률
   * 열이 아니라 프로젝트 열 자리에 떠서 "무엇의 평균인지" 가 위치로 읽히지 않았다.
   * 열 번호를 명시해 두면 조각 하나가 없을 때(지연 0건 등)도 나머지가 제자리를 지킨다.
   */
  return (
    <>
      <span className="mtb-gsum-cell mtb-gsum-cell--status">
        {overdue > 0 && (
          <span className="mtb-gsum-over">{tProject('project.mytask.gsum_overdue', { n: String(overdue) })}</span>
        )}
      </span>

      {/*
        * ⭐⭐ 2026-10-10 — ★ 「값이 ⭐ 있을 때만」으로 바꿨다 (⇒ 사용자 결정).
        *
        * ## ⛔ 종전에는 ★ 이 조각만 ⇒ ⭐ «늘» 그려졌다
        * ```
        * 형제 둘은 ★ 가드가 ⭐ 있었다:
        *   :96  {overdue > 0 && ( … )}
        *   :113 {soonestDiff !== null && ( … )}
        * ⇒ ⛔ 그런데 ★ 진척률만 ⇒ ⭐ 조건 없이 그려 ⇒ ⇒ 그룹 줄마다 ★ 「0%」 ＋ ⭐ 빈 막대가 떴다
        * ```
        * ⇒ ⚠ 그 0 은 ★ ⭐ «거짓이 아니었다» — ⇒ 2026-10-10 에 재서 확인했다:
        * ```
        * prisma/schema.prisma  model Task ⇒ progress Int @default(0)   (⇐ 마이그 219 · 2026-10-06)
        * ⇒ ★ 그런데 그 칸을 ⭐ «쓰는» 끝점이 ⇒ ★ ⭐ 0건이다:
        *     프론트 `PatchTaskInput` 에 progress ⇒ 0
        *     서버   grep -rn progress apps/chat-server/src | grep -E "body|properties|data:" ⇒ 0건
        *     [대조군] 같은 꼴로 `priority` ⇒ ★ 둘(생성 `:483` · 수정 `:682`) ⇒ ⇒ 거르개는 산다
        * ⇒ ⇒ ★ 그리고 `/tasks/me` 가 ⭐ 그 칸을 «안 내놓는다» ⇒ ⇒ 받는 쪽은 늘 `undefined` ⇒ `?? 0`
        * ⇒ ⇒ ⇒ ⭐ 그러므로 ★ 화면의 「0%」는 ⭐ «DB 값과 같다» — ⛔ 틀린 정보가 ★ 아니었다
        * ```
        * ⇒ ⭐ 그래서 ★ 「그대로 보일까 / 값 있을 때만 보일까」는 ⇒ ⭐ «빼는 판단»이고
        *   ⇒ ⇒ ★ 그것은 ⭐ 사용자 몫이다(CLAUDE.md 「우리 기능이 아니라 뺀다는 판단은 AI 가 하지 않는다」).
        *   ⇒ ⇒ ⇒ ✅ 2026-10-10 에 ★ 두 갈래를 올려 ⇒ ⭐ 사용자가 ★ 「값 있을 때만」을 골랐다.
        * ⇒ ⚠ 칸 `<span>` 은 ★ ⭐ 남긴다 — ⇒ 위 머리말이 「⭐ 열 번호를 명시해 두면 조각 하나가
        *   없을 때(지연 0건 등)도 나머지가 ★ 제자리를 지킨다」라고 적는다. ⇒ ⇒ 형제 둘과 ⭐ 같은 꼴이다.
        * ⇒ ⚠⚠ 그리고 ★ 「0 이면 안 그린다」는 ⇒ ⭐ 「서버가 값을 주기 시작하면」 ★ 저절로 다시 그려진다 —
        *   ⇒ ⇒ ⭐ 지울 조건이 ★ 따로 필요 없다. ⇒ ⇒ 그 불변식을 ★ 가드가 잠근다
        *     (`pages/ProjectPage/group-summary-shape.guard.test.tsx`).
        */}
      <span className="mtb-gsum-cell mtb-gsum-cell--prog">
        {avgProgress > 0 && (
          <>
            <span
              className="mtb-gsum-prog"
              role="img"
              aria-label={tProject('project.mytask.gsum_avg', { n: String(avgProgress) })}
            >
              <span className="mtb-gsum-progfill" style={{ width: `${avgProgress}%` }} />
            </span>
            <b className="mtb-gsum-pct">{avgProgress}%</b>
          </>
        )}
      </span>

      <span className="mtb-gsum-cell mtb-gsum-cell--due">
        {soonestDiff !== null && (
          <span className="mtb-gsum-soon">
            {soonestDiff < 0
              ? tProject('project.mytask.gsum_soonest_over', { n: String(Math.abs(soonestDiff)) })
              : tProject('project.mytask.gsum_soonest_left', { n: String(soonestDiff) })}
          </span>
        )}
      </span>
    </>
  );
}

/* ============================================================
 * ③ 행 인라인 액션 — 제목 셀 오른쪽
 * ============================================================ */

/** 하루 뒤 날짜(YYYY-MM-DD). dueDate 가 없으면 오늘부터 센다. */
export function nextDay(dueDate: string | null): string {
  const base = dueDate ? new Date(dueDate) : new Date();
  if (Number.isNaN(base.getTime())) return '';
  base.setDate(base.getDate() + 1);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${base.getFullYear()}-${p(base.getMonth() + 1)}-${p(base.getDate())}`;
}

/**
 * 행에 손이 닿을 때만 나타나는 세 가지.
 *
 * 완료는 넣지 않는다 — 행 왼쪽 동그란 체크가 이미 그 일을 한다. 같은 행에 같은 버튼이
 * 둘이면 2026-08-25 에 걷어낸 '상세' 열과 같은 실수가 된다. 그래서 **체크로는 못 하는 것**만 둔다.
 *
 * 평소에는 opacity:0 이라 목록이 조용하고, hover / focus-within 에서만 드러난다.
 * 키보드로도 닿아야 하므로 focus-within 을 같이 건다(hover 만 걸면 탭 이동 시 안 보인다).
 */
export function RowActions({ task, onPriority, onPostpone, onOpen, onDelete }: {
  task: MyTaskDto;
  onPriority: (t: MyTaskDto) => void;
  /**
   * 2026-09-04 — [📅] 가 '하루 미루기' 에서 '기한 정하기' 로 바뀌었다.
   *
   * 호출부가 날짜 팝오버를 **누른 버튼 옆에** 세울 수 있도록 그 버튼의 화면 좌표를 함께 준다.
   * 두 번째 인자는 **선택**이라, 한 인자만 받던 기존 호출부는 손대지 않아도 그대로 컴파일된다
   * (작업 규칙: 기존 시그니처를 깨지 않고 넓히기만 한다).
   */
  onPostpone: (t: MyTaskDto, anchor?: DOMRect) => void;
  onOpen: (t: MyTaskDto) => void;
  /**
   * 2026-09-01 — 행에서 바로 지우는 [🗑]. **주지 않으면 버튼도 뜨지 않는다**(옵셔널).
   *
   * 되돌릴 수 없는 동작이라 나머지 셋과 붙여 두지 않는다 — 맨 끝에, 한 칸 띄워 세우고
   * (`--danger` 가 margin 을 준다) 손이 닿을 때만 붉어진다. 확인창은 호출부가 띄운다.
   */
  onDelete?: (t: MyTaskDto) => void;
}) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const high = task.priority === 'HIGH';
  /*
   * 2026-09-04 — 완료된 업무에는 [🚩 우선순위]·[📅 기한 하루 미루기] 를 그리지 않는다.
   *
   * 끝난 일의 우선순위를 올리거나 기한을 하루 미루는 것은 뜻이 없는 동작인데, 손이 닿을
   * 때마다 드러나는 자리에 서 있어서 '아직 계획할 것이 남은 일' 처럼 읽혔다. 이 화면은
   * 이미 같은 판단을 두 번 했다 — 완료 행에는 진행률 막대를 안 그리고(취소선이 이미
   * 끝났다고 말한다), 진척률 열도 '-' 로 둔다. 그 규칙을 행 액션에도 그대로 적용한다.
   *
   * [› 열기] 와 [🗑 삭제] 는 남긴다 — 끝난 일도 열어 읽고, 지우는 것은 완료 여부와 무관하게
   * 뜻이 있다. 다시 열어야 하면 행 왼쪽의 동그란 체크가 그 일을 한다(toggleDone).
   */
  const planning = task.status !== 'DONE';
  return (
    <span className="mtb-rowact">
      {planning && (
      <>
      <button
        type="button"
        className={`mtb-rowact-btn${high ? ' mtb-rowact-btn--on' : ''}`}
        data-tip={tProject(high ? 'project.mytask.act_pri_off' : 'project.mytask.act_pri_on')}
        aria-label={tProject(high ? 'project.mytask.act_pri_off' : 'project.mytask.act_pri_on')}
        onClick={(e) => { e.stopPropagation(); onPriority(task); }}
      >
        <Flag size={13} aria-hidden />
      </button>
      <button
        type="button"
        className="mtb-rowact-btn"
        data-tip={tProject('project.mytask.act_postpone')}
        aria-label={tProject('project.mytask.act_postpone')}
        onClick={(e) => {
          e.stopPropagation();
          // 팝오버를 이 버튼 옆에 세우기 위한 기준 좌표.
          onPostpone(task, (e.currentTarget as HTMLElement).getBoundingClientRect());
        }}
      >
        <Calendar size={13} aria-hidden />
      </button>
      </>
      )}
      <button
        type="button"
        className="mtb-rowact-btn"
        data-tip={tProject('project.mytask.act_open')}
        aria-label={tProject('project.mytask.act_open')}
        onClick={(e) => { e.stopPropagation(); onOpen(task); }}
      >
        <ChevronRight size={13} aria-hidden />
      </button>
      {onDelete && (
        <button
          type="button"
          className="mtb-rowact-btn mtb-rowact-btn--danger"
          data-tip={tProject('project.mytask.act_delete')}
          aria-label={tProject('project.mytask.act_delete_aria', { title: task.title })}
          onClick={(e) => { e.stopPropagation(); onDelete(task); }}
        >
          <Trash2 size={13} aria-hidden />
        </button>
      )}
    </span>
  );
}

/* ============================================================
 * ④ 태그 열 — 데이터가 있을 때만
 * ============================================================ */

/** 태그 열을 세울지 — 이 비율에 못 미치면 세우지 않는다. */
export const TAG_COLUMN_MIN_RATIO = 0.3;

/**
 * 태그 열을 세울 것인가.
 *
 * `tags` 는 DTO 에 있는데 이 화면에서 한 번도 쓰이지 않던 값이라 '공짜 잉크' 였다.
 * 다만 무조건 세우면 **태그를 안 쓰는 조직에서는 통째로 빈 열**이 된다 — 이 화면이
 * '보통' 배지와 '상세' 열을 걷어낸 이유가 정확히 그것이었다. 그래서 실제로 붙어 있는
 * 비율이 기준을 넘을 때만 세운다. (2026-08-27 현재 이 DB 는 0건이라 열이 서지 않는다.)
 */
export function shouldShowTagColumn(items: MyTaskDto[]): boolean {
  if (!items.length) return false;
  const tagged = items.filter((t) => (t.tags?.length ?? 0) > 0).length;
  return tagged / items.length >= TAG_COLUMN_MIN_RATIO;
}

/** 태그 칩 — 두 개까지 그리고 나머지는 +n 으로 접는다(행 높이를 지키기 위해). */
export function TagCell({ tags }: { tags: string[] | undefined }) {
  const list = tags ?? [];
  if (!list.length) return <span className="mtb-dash">-</span>;
  const shown = list.slice(0, 2);
  const rest = list.length - shown.length;
  return (
    <span className="mtb-tags">
      {shown.map((t) => <span className="mtb-tag" key={t}>{t}</span>)}
      {rest > 0 && <span className="mtb-tag mtb-tag--more">+{rest}</span>}
    </span>
  );
}
